import React, { useEffect, useRef, useState } from 'react';
import { Building2, Check, Loader2, Search, X } from 'lucide-react';
import type { CompanyIdentity } from '../api/companies';
import { useLanguage } from '../context/LanguageContext';

export const companyName = (company: CompanyIdentity, language: string) =>
  (language === 'kk' ? company.nameKz || company.nameRu : company.nameRu || company.nameKz) || company.bin;

export interface CompanyPickerCopy {
  companySearch: string; companyPlaceholder: string; companyHint: string;
  companyClear: string; companySelected: string; companyBinSelected: string;
  companySearching: string; companyResults: string; companyEmpty: string;
  companySearchError: string; companyLookupBin: string; companyLookupHint: string;
  retry: string; bin: string;
}

export default function CompanyPicker({ value, onChange, loadCompanies, copy: c, id,
  inputValue, onInputChange, allowFreeText = false, allowManualBin = true,
  disabled = false, invalid = false }: {
  value: CompanyIdentity | null;
  onChange: (value: CompanyIdentity | null) => void;
  loadCompanies: (query: string, signal?: AbortSignal) => Promise<{ items: CompanyIdentity[] }>;
  copy: CompanyPickerCopy;
  id: string;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  allowFreeText?: boolean;
  allowManualBin?: boolean;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const { language } = useLanguage();
  const [internalQuery, setInternalQuery] = useState('');
  const query = value ? companyName(value, language) : inputValue ?? internalQuery;
  const setQuery = onInputChange || setInternalQuery;
  const [items, setItems] = useState<CompanyIdentity[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [active, setActive] = useState(-1);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const trimmed = query.trim();
  const listId = `${id}-options`;

  useEffect(() => {
    if (!value) return;
    setOpen(false);
    setActive(-1);
  }, [value, language]);

  useEffect(() => {
    const controller = new AbortController();
    setItems([]); setFailed(false); setActive(-1);
    if (value || disabled || trimmed.length < 2) {
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    const timer = setTimeout(() => {
      loadCompanies(trimmed, controller.signal).then(response => {
        if (controller.signal.aborted) return;
        const seen = new Set<string>();
        setItems(response.items.filter(item => {
          if (!/^\d{12}$/.test(item.bin) || seen.has(item.bin)) return false;
          seen.add(item.bin);
          return true;
        }).slice(0, 8));
      }).catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      }).finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [trimmed, value, disabled, retry, loadCompanies]);

  const canCheckBin = allowManualBin && !loading && /^\d{12}$/.test(trimmed) && !items.some(item => item.bin === trimmed);
  const visible = open && !value && !disabled && trimmed.length >= 2;
  const options = canCheckBin ? [...items, { bin: trimmed, nameRu: null, nameKz: null }] : items;
  const choose = (company: CompanyIdentity) => {
    setOpen(false); setActive(-1);
    onChange(company);
  };
  useEffect(() => {
    if (visible && active >= 0) list.current?.children[active]?.scrollIntoView?.({ block: 'nearest' });
  }, [active, visible]);

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Escape') {
      setOpen(false); setActive(-1);
      event.preventDefault();
    } else if (event.key === 'Tab') {
      setOpen(false);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (value || trimmed.length < 2) return;
      event.preventDefault(); setOpen(true);
      if (options.length) setActive(index => event.key === 'ArrowDown'
        ? (index + 1) % options.length : (index <= 0 ? options.length - 1 : index - 1));
    } else if (visible && event.key === 'Enter') {
      // Never silently pick the first suggestion. Supplier search can submit
      // the entered text; experience calculation requires an explicit company.
      if (active >= 0 && options[active]) {
        event.preventDefault(); choose(options[active]);
      } else if (allowFreeText) {
        setOpen(false);
      } else event.preventDefault();
    } else if (visible && (event.key === 'Home' || event.key === 'End') && options.length) {
      event.preventDefault(); setActive(event.key === 'Home' ? 0 : options.length - 1);
    }
  };

  return <div className="relative min-w-0 flex-1" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }}>
    <label htmlFor={id} className="block text-sm font-medium text-slate-700">{c.companySearch}</label>
    <div className="relative mt-2">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
      <input ref={input} id={id} role="combobox" type="text" autoComplete="off" maxLength={160}
        value={query} disabled={disabled} placeholder={c.companyPlaceholder}
        aria-expanded={visible} aria-controls={visible ? listId : undefined} aria-autocomplete="list"
        aria-activedescendant={visible && active >= 0 && options[active] ? `${id}-option-${active}` : undefined}
        aria-invalid={invalid} aria-describedby={`${id}-hint`} onKeyDown={keyDown}
        onFocus={() => { if (!value) setOpen(true); }}
        onChange={event => { setQuery(event.target.value); setOpen(true); setActive(-1); onChange(null); }}
        className="block min-h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-12 text-base text-slate-900 placeholder:text-sm placeholder:text-slate-400 focus:border-goszakup-600 focus:outline-none focus:ring-2 focus:ring-goszakup-100 disabled:bg-slate-50" />
      {query && <button type="button" disabled={disabled} aria-label={c.companyClear}
        onClick={() => { setQuery(''); onChange(null); setOpen(false); input.current?.focus(); }}
        className="absolute right-0.5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 disabled:opacity-50">
        <X className="h-4 w-4" aria-hidden="true" />
      </button>}
    </div>
    <p id={`${id}-hint`} className="mt-2 flex min-h-5 items-start gap-1.5 text-xs leading-5 text-slate-500">
      {value ? <><Check className="mt-0.5 h-4 w-4 shrink-0 text-goszakup-700" aria-hidden="true" /><span>{value.nameRu || value.nameKz ? c.companySelected : c.companyBinSelected} · {c.bin}: <span className="tabular-nums">{value.bin}</span></span></> : c.companyHint}
    </p>
    <span role="status" aria-live="polite" className="sr-only">{visible && (loading ? c.companySearching : failed ? c.companySearchError : `${c.companyResults} ${items.length}`)}</span>
    {visible && <div className="absolute inset-x-0 top-full z-30 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
      {loading && <p className="flex items-center gap-2 px-4 py-4 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />{c.companySearching}</p>}
      {!loading && failed && <div className="px-4 py-3 text-sm leading-6 text-amber-800"><p>{c.companySearchError}</p><button type="button" onClick={() => { setRetry(value => value + 1); input.current?.focus(); }} className="mt-1 min-h-9 font-semibold underline">{c.retry}</button></div>}
      {!loading && !failed && !items.length && <p className="px-4 py-3 text-sm text-slate-500">{c.companyEmpty}</p>}
      <ul ref={list} id={listId} role="listbox" aria-label={c.companySearch} className="max-h-72 overflow-y-auto overscroll-contain">
        {!loading && options.map((company, index) => {
          const manual = canCheckBin && index === items.length;
          return <li key={`${manual ? 'registry' : 'company'}-${company.bin}`} id={`${id}-option-${index}`} role="option"
            aria-selected={active === index} onMouseDown={event => event.preventDefault()}
            onMouseMove={() => setActive(index)} onClick={() => choose(company)}
            className={`flex cursor-pointer items-start gap-3 px-4 py-3 text-sm ${active === index ? 'bg-goszakup-50 text-goszakup-900' : 'text-slate-800 hover:bg-slate-50'} ${manual ? 'border-t border-slate-200' : ''}`}>
            <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block break-words font-medium">{manual ? c.companyLookupBin : companyName(company, language)}</span>
              <span className="mt-1 block text-xs tabular-nums text-slate-500">{c.bin}: {company.bin}</span>
              {manual && <span className="mt-1 block text-xs leading-5 text-slate-500">{c.companyLookupHint}</span>}
            </span>
          </li>;
        })}
      </ul>
    </div>}
  </div>;
}
